import { useMemo, useState, type ReactNode } from 'react';
import {
    BATTLE_ROUND_OPTIONS,
    DEFAULT_BATTLE_ROUNDS,
    FLEET_MISSION_DEFINITIONS,
    FLEET_SPEED_BOOST_MAX_PERCENT,
    FLEET_SPEED_BOOST_PRICE_PER_PERCENT,
    FLIGHT_SPEED_PERCENT_OPTIONS,
    SHIP_MISSION_RESTRICTIONS,
    SPACE_TRIP_MAX_HOURS,
    SPACE_TRIP_MIN_SECONDS,
    SYSTEMS_PER_GALAXY,
    SYSTEM_POSITION_MAX,
    UNFLYABLE_SHIP_IDS,
    RESOURCE_NAMES,
    getMissionDescription,
    getShipDefinition,
    isTeamPlanet,
    type FleetMissionType,
    type GalaxyPosition,
    type Planet,
    type PlayerRace,
    type ScienceId,
    type SendFleetInput,
    type ShipId,
} from '@kaeliux/shared';
import {
    calculateFleetCargoCapacity,
    calculateFleetFuelConsumption,
    calculateFleetSpeed,
    calculateFlightDistance,
    calculateGasCost,
    calculateMissionFlightSeconds,
    flightDistanceAU,
} from '@kaeliux/game-engine';
import { useFleetStore } from '../store/fleetStore';
import { usePlayerStore } from '../store/playerStore';
import { usePlanetStore } from '../store/planetStore';
import { getShipIcon } from '../utils/shipIcons';
import { formatCoords } from '../utils/fleetFormat';
import { formatDuration } from '../utils/formatters';
import { sendFleet, type SendFleetResult } from '../services/fleet.service';
import { MISSION_ICON } from '../utils/missionMeta';
import { Panel } from './ui/Panel';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import { StatTile } from './ui/StatTile';
import { Badge } from './ui/Badge';
import { Chip } from './ui/Chip';
import { Select, SelectOption } from './ui/Select';
import ResourceQuantities from './ResourceQuantities';
import { resourceCostGlyph } from './resourceCostIcons';
import { cn } from '../utils/cn';
import { FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES } from './ui/fieldStyles';
import AccountLockNotice from './auth/AccountLockNotice';
import type { MissionLockNotice } from '../hooks/useAccountStanding';

/**
 * Dispatch form for sending a fleet from a given planet. Used by both
 * FleetsPage (player's own planets) and TeamPlanetFleetsPage (alliance
 * Team planet). The store read (`usePlanetStore`) is for target-planet
 * suggestions only — the origin planet is always the `planet` prop, not
 * derived from the store, so TeamPlanetFleetsPage works without routing
 * the origin through the app-wide game loop.
 */
interface SendFleetFormProps {
    /** Origin planet the fleet launches from. */
    planet: Planet;
    race: PlayerRace;
    sciences: Partial<Record<ScienceId, number>>;
    shipUpgrades: Partial<Record<ShipId, number>>;
    darkMatter?: number;
    flightMultiplier: number;
    allowedMissions: [FleetMissionType, ...FleetMissionType[]];
    initialMission?: FleetMissionType;
    /** Missions this account can't send yet (issue #332) — selectable, so the player sees why, but never sendable. */
    missionLocks?: Partial<Record<FleetMissionType, MissionLockNotice>>;
    initialTarget?: GalaxyPosition;
    emptyShipsMessage?: ReactNode;
    onSent: (result: SendFleetResult) => void;
}

const SPEED_MIN = Math.min(...FLIGHT_SPEED_PERCENT_OPTIONS);
const SPEED_MAX = Math.max(...FLIGHT_SPEED_PERCENT_OPTIONS);
const SPEED_STEP = FLIGHT_SPEED_PERCENT_OPTIONS[0] - FLIGHT_SPEED_PERCENT_OPTIONS[1];

function isShipAllowedForMission(shipId: ShipId, race: PlayerRace, mission: FleetMissionType): boolean {
    const def = FLEET_MISSION_DEFINITIONS[mission];

    const shipRestrictions = SHIP_MISSION_RESTRICTIONS[shipId];
    if (shipRestrictions && !shipRestrictions.includes(mission)) return false;

    if (def.onlyShipIds && !def.onlyShipIds.includes(shipId)) return false;

    if (def.onlyShipCategory) {
        const shipDef = getShipDefinition(race, shipId);
        if (shipDef.category !== def.onlyShipCategory) return false;
    }

    return true;
}

function planetImage(planetType: number): string {
    return new URL(`../assets/images/planets/planet_${planetType}.png`, import.meta.url).href;
}

export default function SendFleetForm({
    planet,
    race,
    sciences,
    shipUpgrades,
    darkMatter = 0,
    flightMultiplier,
    allowedMissions,
    initialMission,
    missionLocks = {},
    initialTarget,
    emptyShipsMessage = 'No ships docked at this planet.',
    onSent,
}: SendFleetFormProps) {
    const [selected, setSelected] = useState<Partial<Record<ShipId, number>>>({});
    const [mission, setMission] = useState<FleetMissionType>(
        initialMission && allowedMissions.includes(initialMission)
            ? initialMission
            : allowedMissions.find((m) => !missionLocks[m]) ?? allowedMissions[0],
    );
    const [target, setTarget] = useState<GalaxyPosition>(initialTarget ?? { galaxy: 1, system: 1, position: 1 });
    const [speedPercent, setSpeedPercent] = useState<number>(100);
    const [speedBoostPercent, setSpeedBoostPercent] = useState<number>(0);
    const [cargo, setCargo] = useState({ metal: 0, minerals: 0, gas: 0, scrap: 0 });
    const [battleRounds, setBattleRounds] = useState<number>(DEFAULT_BATTLE_ROUNDS);
    const [holdHours, setHoldHours] = useState(0);
    const [holdMinutes, setHoldMinutes] = useState(SPACE_TRIP_MIN_SECONDS / 60);
    const [isSending, setIsSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);

    const def = FLEET_MISSION_DEFINITIONS[mission];
    const missionLock = missionLocks[mission];
    const allPlanets = usePlanetStore((s) => s.planets);
    // A Team planet only flies Sun Attack/Space Trip — never to a member's own planets.
    const ownPlanetTargets = isTeamPlanet(planet) ? [] : allPlanets;

    const dockedShips = useMemo(() => {
        const entries = (Object.entries(planet.ships ?? {}) as [string, number][])
            .map(([key, count]) => [Number(key) as ShipId, count] as const)
            .filter(([shipId, count]) => count > 0 && !UNFLYABLE_SHIP_IDS.includes(shipId))
            .sort(([a], [b]) => a - b);
        return entries;
    }, [planet.ships]);

    const selectedManifest = useMemo(() => {
        const manifest: Partial<Record<ShipId, number>> = {};
        for (const [shipId, docked] of dockedShips) {
            if (!isShipAllowedForMission(shipId, race, mission)) continue;
            const count = Math.min(selected[shipId] ?? 0, docked);
            if (count > 0) manifest[shipId] = count;
        }
        return manifest;
    }, [selected, dockedShips, race, mission]);

    const anySelected = Object.keys(selectedManifest).length > 0;

    const speedBoostAllowed = !def.manualDuration && mission !== 'espionage' && mission !== 'sunAttack';
    const speedBoostCost = speedBoostAllowed ? speedBoostPercent * FLEET_SPEED_BOOST_PRICE_PER_PERCENT : 0;
    const speedBoostAffordable = speedBoostCost <= darkMatter;

    const estimates = useMemo(() => {
        if (!anySelected) return null;
        const speed = calculateFleetSpeed(race, selectedManifest, sciences, shipUpgrades);
        const capacity = calculateFleetCargoCapacity(race, selectedManifest, shipUpgrades);
        const population = (Object.entries(selectedManifest) as [string, number][])
            .reduce((sum, [key, count]) => sum + getShipDefinition(race, Number(key) as ShipId).hangar * count, 0);
        const resolvedTarget: GalaxyPosition = def.manualDuration
            ? planet.coordinates
            : def.forcesSunTarget
                ? { ...target, position: 0 }
                : target;
        const distance = def.manualDuration ? 0 : calculateFlightDistance(planet.coordinates, resolvedTarget);
        let oneWaySeconds: number | null = null;
        try {
            oneWaySeconds = calculateMissionFlightSeconds(
                mission,
                planet.coordinates,
                resolvedTarget,
                speedPercent,
                speed,
                { flightMultiplier, speedMotivationPercent: speedBoostAllowed ? speedBoostPercent : 0 },
                holdHours * 3_600 + holdMinutes * 60,
            );
        } catch {
            oneWaySeconds = null;
        }
        const fuelReduction = Math.min(Math.max(sciences.chemistry ?? 0, 0), 15) * 0.05;
        const gas = calculateGasCost(
            mission,
            calculateFleetFuelConsumption(race, selectedManifest),
            distance,
            speedPercent,
            fuelReduction,
        );
        const loaded = cargo.metal + cargo.minerals + cargo.gas + cargo.scrap;
        return { speed, capacity, population, distance, oneWaySeconds, gas, loaded };
    }, [anySelected, selectedManifest, race, sciences, shipUpgrades, def, target, mission, speedPercent, speedBoostPercent, speedBoostAllowed, flightMultiplier, holdHours, holdMinutes, cargo, planet.coordinates]);

    async function handleSend() {
        setIsSending(true);
        setSendError(null);
        try {
            const ships: Record<string, number> = {};
            for (const [shipId, count] of Object.entries(selectedManifest)) {
                ships[shipId] = count as number;
            }
            const input: SendFleetInput = {
                originPlanetId: planet.id,
                mission,
                ships,
                target: def.forcesSunTarget ? { ...target, position: 0 } : target,
                speedPercent: speedPercent as SendFleetInput['speedPercent'],
                speedBoostPercent: speedBoostAllowed ? speedBoostPercent : 0,
                cargo: def.allowsCargo ? cargo : { metal: 0, minerals: 0, gas: 0, scrap: 0 },
                ...(def.allowsBattleRounds ? { battleRounds: battleRounds as SendFleetInput['battleRounds'] } : {}),
                ...(def.manualDuration ? { holdHours, holdMinutes } : {}),
            };
            const result = await sendFleet(input, { flightMultiplier });
            useFleetStore.getState().applyView({
                fleets: result.fleets,
                activeCount: result.activeCount,
                maxActive: result.maxActive,
                espionageCount: result.espionageCount,
                maxEspionage: result.maxEspionage,
            });
            if (result.premium) {
                usePlayerStore.setState({ premium: result.premium });
            }
            onSent(result);
            setSelected({});
            setCargo({ metal: 0, minerals: 0, gas: 0, scrap: 0 });
            setSpeedBoostPercent(0);
        } catch (err) {
            setSendError(err instanceof Error ? err.message : 'Failed to send fleet');
        } finally {
            setIsSending(false);
        }
    }

    function setCargoMax(key: keyof typeof cargo) {
        if (!estimates) return;
        const otherLoaded = estimates.loaded - cargo[key];
        const room = Math.max(0, estimates.capacity - otherLoaded);
        const available = key === 'gas'
            ? Math.max(0, Math.floor(planet.resources.gas.current) - estimates.gas)
            : key === 'scrap'
                ? Math.floor(planet.resources.scrap.current)
                : Math.floor(planet.resources[key].current);
        setCargo({ ...cargo, [key]: Math.min(room, available) });
    }

    function selectPlanetTarget(p: Planet) {
        setTarget(p.coordinates);
    }

    const inlineInputClass = cn(FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES, 'px-2 py-1 text-body');

    return (
        <Panel className="flex flex-col gap-5">
            <h2 className="text-title text-content-primary font-semibold">Send Fleet</h2>

            {/* ─── Ship selection (cards grid) ─── */}
            <div className="flex flex-col gap-2">
                {dockedShips.length === 0 ? (
                    <p className="text-content-muted text-body">{emptyShipsMessage}</p>
                ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-2">
                        {dockedShips.map(([shipId, docked]) => {
                            const shipDef = getShipDefinition(race, shipId);
                            const allowed = isShipAllowedForMission(shipId, race, mission);
                            const count = allowed ? (selected[shipId] ?? 0) : 0;
                            const isActive = count > 0;
                            return (
                                <div
                                    key={shipId}
                                    className={cn(
                                        'flex flex-col items-center rounded-tile border bg-surface-chip p-2 transition-colors',
                                        allowed
                                            ? isActive
                                                ? 'border-[color:var(--zone-accent)] bg-[color:var(--zone-accent)]/10'
                                                : 'border-hairline hover:border-hairline-strong'
                                            : 'border-hairline opacity-40 pointer-events-none',
                                    )}
                                    data-testid={`fleet-ship-${shipId}`}
                                >
                                    <div className="w-16 h-16 rounded-control bg-surface-void/60 overflow-hidden shrink-0 mb-1.5">
                                        <img
                                            src={getShipIcon(race, shipId)}
                                            alt={shipDef.name}
                                            className="w-full h-full object-contain drop-shadow-sprite"
                                        />
                                    </div>
                                    <span className="text-label text-content-primary text-center leading-tight truncate w-full" title={shipDef.name}>
                                        {shipDef.name}
                                    </span>
                                    <div className="flex items-center gap-1 mt-1">
                                        <input
                                            type="number"
                                            min={0}
                                            max={docked}
                                            value={count}
                                            onChange={(e) => {
                                                const v = Math.max(0, Math.min(docked, Number(e.target.value) || 0));
                                                setSelected({ ...selected, [shipId]: v });
                                            }}
                                            className={cn(FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES, 'w-16 px-1 py-0.5 text-center text-label h-6')}
                                            aria-label={`${shipDef.name} count`}
                                            disabled={!allowed}
                                        />
                                    </div>
                                    <span className="text-micro text-content-muted">(Max {docked})</span>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        className="mt-1 w-full"
                                        onClick={() => setSelected({ ...selected, [shipId]: count >= docked ? 0 : docked })}
                                        disabled={!allowed}
                                    >
                                        {count >= docked ? 'Clear' : 'Max'}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}
                {estimates && (
                    <p className="text-content-muted text-label">
                        Population: {estimates.population.toLocaleString()} · Cargo capacity: {estimates.capacity.toLocaleString()}
                    </p>
                )}
            </div>

            {/* ─── Mission / Target / Load row ─── */}
            <div className="flex flex-col lg:flex-row gap-4">
                {/* ─── §1 Mission ─── */}
                <Panel variant="glass" padding="sm" className="flex flex-col gap-3 lg:flex-1">
                    <div className="flex items-center gap-1.5">
                        <h3 className="text-body text-content-primary font-semibold flex items-center gap-1.5">
                            <Icon name="fleets" size={16} className="text-[color:var(--zone-accent)]" />
                            Mission
                        </h3>
                        <button
                            type="button"
                            aria-disabled="true"
                            className="ml-auto inline-flex items-center gap-1 opacity-50"
                            data-testid="reorder-missions-placeholder"
                            onClick={(e) => e.preventDefault()}
                        >
                            <Icon name="reorder" size={14} className="text-content-muted" />
                            <Badge tone="neutral">Coming soon</Badge>
                        </button>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                        {allowedMissions.map((m) => {
                            const mDef = FLEET_MISSION_DEFINITIONS[m];
                            const isActive = mission === m;
                            return (
                                <Chip
                                    key={m}
                                    onClick={() => { setMission(m); setSendError(null); }}
                                    leadingIcon={<Icon name={MISSION_ICON[m]} className="w-5 h-5 sm:w-3.5 sm:h-3.5" />}
                                    className={cn(
                                        isActive
                                            ? 'bg-[color:var(--zone-accent)]/20 border-[color:var(--zone-accent)] text-content-primary'
                                            : 'text-content-muted hover:text-content-secondary hover:border-hairline-strong',
                                    )}
                                    data-testid={`mission-btn-${m}`}
                                    data-locked={missionLocks[m] ? 'true' : undefined}
                                >
                                    <span className="hidden sm:inline">{mDef.name}</span>
                                    {missionLocks[m] && <Icon name="lock" size={12} className="text-status-warning" aria-label="Locked" />}
                                </Chip>
                            );
                        })}
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <Select
                            value={mission}
                            onChange={(e) => { setMission(e.target.value as FleetMissionType); setSendError(null); }}
                            className="w-full"
                            data-testid="mission-select"
                        >
                            {allowedMissions.map((m) => (
                                <SelectOption key={m} value={m}>{FLEET_MISSION_DEFINITIONS[m].name}</SelectOption>
                            ))}
                        </Select>
                        <span className="text-content-muted text-label">{getMissionDescription(mission, race)}</span>
                    </div>

                    {missionLock && (
                        <AccountLockNotice showCreateAccount={missionLock.guest} data-testid="mission-lock-notice">
                            {missionLock.message}
                        </AccountLockNotice>
                    )}

                    {def.allowsBattleRounds && (
                        <label className="flex items-center gap-2 text-content-muted text-label">
                            Battle rounds
                            <Select
                                value={battleRounds}
                                onChange={(e) => setBattleRounds(Number(e.target.value))}
                                className="w-20"
                                aria-label="Battle rounds"
                            >
                                {BATTLE_ROUND_OPTIONS.map((v) => (
                                    <SelectOption key={v} value={v}>{v}</SelectOption>
                                ))}
                            </Select>
                        </label>
                    )}

                    {def.manualDuration && (
                        <label className="flex items-center gap-2 text-content-muted text-label flex-wrap">
                            Trip duration (one way)
                            <div className="flex items-center gap-1">
                                <Select
                                    value={holdHours}
                                    onChange={(e) => setHoldHours(Number(e.target.value))}
                                    className="w-20"
                                    aria-label="Trip hours"
                                >
                                    {Array.from({ length: SPACE_TRIP_MAX_HOURS + 1 }, (_, h) => (
                                        <SelectOption key={h} value={h}>{h} h</SelectOption>
                                    ))}
                                </Select>
                                <Select
                                    value={holdMinutes}
                                    onChange={(e) => setHoldMinutes(Number(e.target.value))}
                                    className="w-24"
                                    aria-label="Trip minutes"
                                >
                                    {Array.from({ length: 60 }, (_, m) => (
                                        <SelectOption key={m} value={m}>{m} min</SelectOption>
                                    ))}
                                </Select>
                            </div>
                        </label>
                    )}
                </Panel>

                {/* ─── §2 Target ─── */}
                <Panel
                    variant="glass"
                    padding="sm"
                    className={cn(
                        'flex flex-col gap-3 lg:flex-1',
                        def.manualDuration && 'hidden lg:flex opacity-40 pointer-events-none',
                    )}
                >
                        <h3 className="text-body text-content-primary font-semibold flex items-center gap-1.5">
                            <Icon name="planet" size={16} className="text-[color:var(--zone-accent)]" />
                            Target
                        </h3>

                        <div className="flex flex-col gap-1.5">
                            <span className="text-label text-content-muted">Coordinates</span>
                            <div className="flex items-center gap-1">
                                <input
                                    type="number" min={1} max={9} value={target.galaxy}
                                    onChange={(e) => setTarget({ ...target, galaxy: Number(e.target.value) || 1 })}
                                    className={`${inlineInputClass} w-14 text-center`}
                                    aria-label="Target galaxy"
                                />
                                <span className="text-content-muted text-body">:</span>
                                <input
                                    type="number" min={1} max={SYSTEMS_PER_GALAXY} value={target.system}
                                    onChange={(e) => setTarget({ ...target, system: Number(e.target.value) || 1 })}
                                    className={`${inlineInputClass} w-14 text-center`}
                                    aria-label="Target system"
                                />
                                <span className="text-content-muted text-body">:</span>
                                <input
                                    type="number" min={0} max={SYSTEM_POSITION_MAX}
                                    value={def.forcesSunTarget ? 0 : target.position}
                                    disabled={def.forcesSunTarget}
                                    onChange={(e) => setTarget({ ...target, position: Number(e.target.value) || 0 })}
                                    className={`${inlineInputClass} w-14 text-center`}
                                    aria-label="Target position"
                                />
                            </div>
                            <span className="text-content-muted text-micro">
                                Origin: {formatCoords(planet.coordinates)}
                            </span>
                        </div>

                        {ownPlanetTargets.length > 0 && (
                            <div className="flex flex-col gap-1.5">
                                <span className="text-label text-content-muted">Your planets</span>
                                <div className="flex flex-wrap gap-2">
                                    {ownPlanetTargets.map((p) => {
                                        const coords = formatCoords(p.coordinates);
                                        const isCurrentTarget =
                                            target.galaxy === p.coordinates.galaxy &&
                                            target.system === p.coordinates.system &&
                                            target.position === p.coordinates.position;
                                        const isSelf = p.id === planet.id;
                                        return (
                                            <Chip
                                                key={p.id}
                                                onClick={() => selectPlanetTarget(p)}
                                                leadingIcon={
                                                    <img
                                                        src={planetImage(p.planetType)}
                                                        alt=""
                                                        className="w-6 h-6 rounded-full object-cover"
                                                    />
                                                }
                                                className={cn(
                                                    isCurrentTarget
                                                        ? 'bg-[color:var(--zone-accent)]/20 border-[color:var(--zone-accent)] text-content-primary'
                                                        : 'text-content-muted hover:text-content-secondary hover:border-hairline-strong',
                                                )}
                                                data-testid={`planet-target-${p.id}`}
                                            >
                                                <span className="truncate max-w-[8rem]">{p.name}</span>
                                                <Badge tone={isSelf ? 'accent' : 'neutral'}>{coords}</Badge>
                                            </Chip>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between">
                                <span className="text-label text-content-muted">Speed</span>
                                <span className="text-value text-content-primary">{speedPercent}%</span>
                            </div>
                            <input
                                type="range"
                                min={SPEED_MIN}
                                max={SPEED_MAX}
                                step={SPEED_STEP}
                                value={speedPercent}
                                onChange={(e) => setSpeedPercent(Number(e.target.value))}
                                className="w-full accent-[color:var(--zone-accent)] h-2 bg-space-950 rounded-full appearance-none cursor-pointer
                                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                                    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-content-primary [&::-webkit-slider-thumb]:shadow-e2
                                    [&::-webkit-slider-runnable-track]:rounded-full"
                                aria-label="Fleet speed"
                                data-testid="speed-slider"
                            />
                            <div className="flex justify-between text-micro text-content-muted">
                                <span>{SPEED_MIN}%</span>
                                <span>{SPEED_MAX}%</span>
                            </div>
                        </div>

                        {speedBoostAllowed && (
                            <div className="flex flex-col gap-1.5">
                                <div className="flex items-center justify-between">
                                    <span className="text-label flex items-center gap-1.5" data-testid="speed-boost-cost">
                                        <span className="text-content-muted">Speed boost</span>
                                        <Icon name="dark-matter" size={14} className="text-status-warning" />
                                        {speedBoostPercent > 0
                                            && <span className="text-title text-status-warning">{speedBoostCost} Dark Matter</span>}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="number"
                                            min={0}
                                            max={FLEET_SPEED_BOOST_MAX_PERCENT}
                                            step={1}
                                            value={speedBoostPercent}
                                            onChange={(e) => {
                                                const v = Math.max(0, Math.min(FLEET_SPEED_BOOST_MAX_PERCENT, Math.round(Number(e.target.value) || 0)));
                                                setSpeedBoostPercent(v);
                                            }}
                                            className={`${inlineInputClass} w-14 text-center`}
                                            aria-label="Speed boost percent"
                                            data-testid="speed-boost-input"
                                        />
                                        <span className="text-value text-content-primary">%</span>
                                    </div>
                                </div>
                                <input
                                    type="range"
                                    min={0}
                                    max={FLEET_SPEED_BOOST_MAX_PERCENT}
                                    step={1}
                                    value={speedBoostPercent}
                                    onChange={(e) => setSpeedBoostPercent(Number(e.target.value))}
                                    className="w-full accent-[color:var(--zone-accent)] h-2 bg-space-950 rounded-full appearance-none cursor-pointer
                                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                                        [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-content-primary [&::-webkit-slider-thumb]:shadow-e2
                                        [&::-webkit-slider-runnable-track]:rounded-full"
                                    aria-label="Speed boost slider"
                                />
                                <div className="flex justify-between text-micro text-content-muted">
                                    <span>0%</span>
                                    <span>50%</span>
                                </div>
                                {!speedBoostAffordable && (
                                    <span className="text-status-danger text-micro" data-testid="speed-boost-unaffordable">
                                        Not enough Dark Matter for this boost
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Load boost placeholder */}
                        {speedBoostAllowed && (
                            <div className="flex flex-col gap-1.5 opacity-50 pointer-events-none" data-testid="load-boost-placeholder">
                                <div className="flex items-center justify-between">
                                    <span className="text-label flex items-center gap-1.5">
                                        <span className="text-content-muted">Load boost</span>
                                        <Icon name="dark-matter" size={14} className="text-status-warning" />
                                        <Badge tone="neutral">Coming soon</Badge>
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={0}
                                            disabled
                                            className={`${inlineInputClass} w-14 text-center`}
                                            aria-label="Load boost percent"
                                        />
                                        <span className="text-value text-content-primary">%</span>
                                    </div>
                                </div>
                                <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={0}
                                    disabled
                                    className="w-full accent-[color:var(--zone-accent)] h-2 bg-space-950 rounded-full appearance-none cursor-pointer
                                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                                        [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-content-primary [&::-webkit-slider-thumb]:shadow-e2
                                        [&::-webkit-slider-runnable-track]:rounded-full"
                                    aria-label="Load boost slider"
                                />
                                <div className="flex justify-between text-micro text-content-muted">
                                    <span>0%</span>
                                    <span>100%</span>
                                </div>
                            </div>
                        )}
                    </Panel>

                {/* ─── §3 Load ─── */}
                <Panel
                    variant="glass"
                    padding="sm"
                    className={cn(
                        'flex flex-col gap-3 lg:flex-1',
                        !def.allowsCargo && 'hidden lg:flex opacity-40 pointer-events-none',
                    )}
                >
                    <h3 className="text-body text-content-primary font-semibold flex items-center gap-1.5">
                        <Icon name="cargo" size={16} className="text-[color:var(--zone-accent)]" />
                        Load
                    </h3>
                    <div className="flex flex-col gap-3">
                        {(['metal', 'minerals', 'gas', 'scrap'] as const).map((key) => (
                            <label key={key} className="flex flex-col gap-1 text-label">
                                <span className="flex items-center gap-1 text-content-muted">
                                    {resourceCostGlyph(key)}
                                    <span>{RESOURCE_NAMES[key]}</span>
                                </span>
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number" min={0} value={cargo[key]}
                                        onChange={(e) => setCargo({ ...cargo, [key]: Math.max(0, Number(e.target.value) || 0) })}
                                        className={`${inlineInputClass} w-full`}
                                        aria-label={`Load ${RESOURCE_NAMES[key]}`}
                                        disabled={!def.allowsCargo}
                                    />
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setCargoMax(key)}
                                        disabled={!estimates || !def.allowsCargo}
                                    >
                                        Max
                                    </Button>
                                </div>
                            </label>
                        ))}
                    </div>
                    {estimates && def.allowsCargo && (
                        <p className="text-content-muted text-label">
                            Loaded: {estimates.loaded.toLocaleString()} / {estimates.capacity.toLocaleString()}
                            {estimates.loaded > estimates.capacity && (
                                <span className="text-status-danger"> — over capacity</span>
                            )}
                        </p>
                    )}
                </Panel>
            </div>

            {/* ─── Flight summary ─── */}
            {estimates && (
                <div className="flex flex-wrap gap-3" data-testid="flight-summary">
                    <StatTile
                        accent="military"
                        icon={<Icon name="speed" size={17} />}
                        value={
                            def.manualDuration || estimates.distance === 0
                                ? '—'
                                : `${flightDistanceAU(estimates.distance).toLocaleString()} AU`
                        }
                        sublabel="Distance"
                    />
                    <StatTile
                        accent="military"
                        icon={<Icon name="fleets" size={17} />}
                        value={
                            estimates.oneWaySeconds === null
                                ? '—'
                                : `${formatDuration(estimates.oneWaySeconds)} / ${formatDuration(estimates.oneWaySeconds * 2)}`
                        }
                        sublabel="One way / round trip"
                    />
                    <StatTile
                        accent="gas"
                        icon={<Icon name="gas" size={17} />}
                        value={<ResourceQuantities amounts={{ gas: estimates.gas }} includeZero />}
                        sublabel={`${RESOURCE_NAMES.gas} needed`}
                    />
                    <StatTile
                        accent="military"
                        icon={<Icon name="speed" size={17} />}
                        value={estimates.speed.toLocaleString()}
                        sublabel="Fleet speed"
                    />
                </div>
            )}

            {/* ─── Send button ─── */}
            {sendError && <p className="text-status-danger text-body" data-testid="send-error" role="alert">{sendError}</p>}

            <div>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleSend}
                    disabled={!anySelected || isSending || !speedBoostAffordable || !!missionLock}
                    loading={isSending}
                    data-testid="send-fleet-button"
                >
                    Send Fleet
                </Button>
                {!anySelected && (
                    <span className="text-content-muted text-label ml-3">Select at least one ship</span>
                )}
                {anySelected && !speedBoostAffordable && (
                    <span className="text-status-danger text-label ml-3">Not enough Dark Matter for the speed boost</span>
                )}
            </div>
        </Panel>
    );
}
